import type {
  DeactivatedProfilesStatsResponse,
  ExclusionKeyword,
  ProfileResponse,
} from '@shared/schemas';

import { type DeactivatedFilters, NO_FILTERS } from '../domain/deactivated-filters';

/** Rows per page, matching the original `useInfiniteApi('/profiles/deactivated', 25)` default. */
export const DEFAULT_PAGE_SIZE = 25;

/** Bulk-restore lifecycle (`error` holds the failure snackbar message). */
export type RestoreStatus = 'idle' | 'restoring' | 'succeeded' | 'failed';

export interface RestoreState {
  status: RestoreStatus;
  /** Rows restored (drives the success snackbar count). */
  count: number;
  /**
   * Of those, rows that were scan/enrichment-deactivated and are now immunised
   * against future scans; when > 0 the snackbar says so, the one moment the
   * admin can learn that restoring immunises.
   */
  immunised: number;
  error: string | null;
}

/** Manual exclusion-scan lifecycle (`error` holds the failure snackbar message). */
export type ScanStatus = 'idle' | 'scanning' | 'succeeded' | 'failed';

export interface ScanState {
  status: ScanStatus;
  /** Profiles deactivated by the scan (drives the success snackbar count). */
  count: number;
  /** Matching profiles the scan skipped because they are immunised (restored earlier). */
  bypassed: number;
  error: string | null;
}

/** Preview-count lifecycle; loaded when the confirm dialog opens, before the scan. */
export type PreviewStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface PreviewState {
  status: PreviewStatus;
  /** How many profiles the scan would deactivate right now. */
  count: number;
  /**
   * Matching profiles the scan would SKIP because they are immunised
   * (`exclusion_bypassed`, set by restoring a scan-deactivated profile). Shown in
   * the confirm dialog so a surprising "0" explains itself instead of reading as
   * a broken scan.
   */
  bypassed: number;
}

/**
 * Blocklist state for the manage-keywords dialog. `mutating` guards add/delete
 * (disables the input); `error` carries the inline message (incl. the 409 on a
 * duplicate word).
 */
export interface KeywordsState {
  items: ExclusionKeyword[];
  loading: boolean;
  error: string | null;
  mutating: boolean;
}

/**
 * The Reactivated tab's list: profiles restored from the deactivated list and
 * still active. Its own cursor and lifecycle; the two tabs paginate
 * independently, and a failed load here must not raise the other tab's banner.
 */
export interface ReactivatedListState {
  rows: ProfileResponse[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
}

/**
 * Redux state for the Deactivated Profiles list. Server-side pagination AND
 * server-side filtering: the slice holds the current page's rows, the grand `total`, the load
 * lifecycle (`error` is a banner message), the current page / pageSize (`page` is
 * 0-indexed), and the bulk-restore lifecycle. Column config and row selection stay
 * local to the page (view state); every field here is serialisable.
 */
export interface DeactivatedProfilesState {
  rows: ProfileResponse[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  /**
   * What the toolbar is narrowing by (#536). In the slice and not in the page,
   * because the FETCH depends on it: the saga reads it back the same way it reads
   * the cursor, and a filter held in view state would leave the two sources of
   * truth to drift.
   */
  filters: DeactivatedFilters;
  restore: RestoreState;
  /**
   * Header aggregates, `null` until they land (and if they never do). They have no
   * `error` field on purpose: a failed aggregate is not worth a banner over a table
   * that loaded fine, so the band simply does not render. `error` above stays about
   * the list.
   */
  stats: DeactivatedProfilesStatsResponse | null;
  /** Manual scan lifecycle (Run exclusion scan). */
  scan: ScanState;
  /** Preview count shown in the scan-confirm dialog. */
  preview: PreviewState;
  /** Exclusion-keyword blocklist (manage-keywords dialog). */
  keywords: KeywordsState;
  /** The Reactivated tab (#512). Loaded when the tab opens, not on mount. */
  reactivated: ReactivatedListState;
  /**
   * The type names the Type filter offers (#702). Empty until loaded, and if the
   * read fails: the filter is then simply absent.
   */
  profileTypes: string[];
}

export const emptyRestore: RestoreState = { status: 'idle', count: 0, immunised: 0, error: null };
export const emptyScan: ScanState = { status: 'idle', count: 0, bypassed: 0, error: null };
export const emptyPreview: PreviewState = { status: 'idle', count: 0, bypassed: 0 };
export const emptyKeywords: KeywordsState = {
  items: [],
  loading: false,
  error: null,
  mutating: false,
};
export const emptyReactivated: ReactivatedListState = {
  rows: [],
  total: 0,
  loading: false,
  error: null,
  page: 0,
  pageSize: DEFAULT_PAGE_SIZE,
};

export const initialState: DeactivatedProfilesState = {
  rows: [],
  total: 0,
  loading: false,
  error: null,
  page: 0,
  pageSize: DEFAULT_PAGE_SIZE,
  filters: NO_FILTERS,
  restore: emptyRestore,
  stats: null,
  scan: emptyScan,
  preview: emptyPreview,
  keywords: emptyKeywords,
  reactivated: emptyReactivated,
  profileTypes: [],
};
